import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PMAvatar, PMButton, PMFormActions, PMIcon } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useLocation, usePeople, useReminder } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { levelDef } from "@/lib/levels";
import { LocationMap } from "@/ui/LocationMap";
import { Modal } from "@/ui/Modal";
import { avatarColor, fullName, relTime, shortDate } from "@/ui/format";
import { dueLabel, isClosed, splitDetails, typeOf, addDays } from "./taskModel";

// Task detail: everything about one task in one place, with the next action
// (complete / reopen) always first. Rendered as the right pane of the Tasks
// inbox, or as a sheet on narrow screens.
export function TaskDetail({ id, onClose, remainingToday }: { id: string; onClose: () => void; remainingToday: number }) {
  const { teamId, hierarchy } = useApp();
  const nav = useNavigate();
  const m = useMutations();
  const t = useReminder(id).data;
  const people = usePeople().data;
  const loc = useLocation(t?.map_feature_id ?? "").data;
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!t) return <div className="td td--empty"><p className="hint">This task isn't available. It may have been deleted.</p></div>;

  const { title, notes } = splitDetails(t.details);
  const type = typeOf(t);
  const closed = isClosed(t);
  const overdue = !closed && (t.reminder_date || addDays(0)) < addDays(0);
  const person = (uid?: string | null) => people.find((p) => p.user_id === uid);
  const assignee = person(t.assigned_user_id);
  const creator = person(t.creator_user_id);
  const d = levelDef(t.location_level, hierarchy.key);

  return (
    <aside className="td" aria-label="Task details">
      <header className="td-head">
        <span className={`td-type ${type.tone}`}><PMIcon name={type.icon} size={16} /> {type.label}</span>
        <span className={`td-status ${closed ? "is-done" : overdue ? "is-late" : ""}`}>{t.completed_at ? "Completed" : t.canceled_at ? "Canceled" : overdue ? "Overdue" : "Open"}</span>
        <span className="spacer" />
        <button className="iconbtn" onClick={onClose} aria-label="Close task"><PMIcon name="close" size={18} /></button>
      </header>

      <h2 className={`td-title ${closed ? "is-done" : ""}`}>{title}</h2>

      <div className="td-primary">
        {t.canceled_at ? (
          <PMButton variant="outline" icon="refresh" onClick={() => m.reinstateReminder(t.id)}>Reinstate</PMButton>
        ) : t.completed_at ? (
          <PMButton variant="outline" icon="refresh" onClick={() => m.reinstateReminder(t.id)}>Reopen task</PMButton>
        ) : (
          <PMButton icon="check-circle" onClick={() => m.completeReminder(t.id, remainingToday)}>Mark complete</PMButton>
        )}
      </div>

      <dl className="td-facts">
        <div><dt>Due</dt><dd className={overdue ? "is-late" : ""}>{dueLabel(t)}{t.reminder_date && <span className="hint"> · {relTime(t.reminder_date + "T" + (t.reminder_time || "09:00:00"))}</span>}</dd></div>
        <div><dt>Assigned to</dt><dd>{assignee ? <span className="td-person"><PMAvatar name={fullName(assignee)} size={24} color={avatarColor(assignee.user_id)} />{fullName(assignee)}</span> : "Unassigned"}</dd></div>
        <div><dt>{d.label}</dt><dd>{t.location_label ? <button className="linkbtn td-link" onClick={() => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: t.map_feature_id! } })}>{d.emoji} {t.location_label}{t.parent_label ? `, ${t.parent_label}` : ""}</button> : "—"}</dd></div>
        {t.field_note_id && <div><dt>From visit</dt><dd><button className="linkbtn td-link" onClick={() => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: t.field_note_id! } })}>{t.visit_title || "Field visit"} · {shortDate(t.visit_started_at)}</button></dd></div>}
        <div><dt>Created</dt><dd>{relTime(t.created_at)}{creator ? ` by ${fullName(creator)}` : ""}{t.ai_created ? " from the visit recording" : ""}</dd></div>
        {t.completed_at && <div><dt>Completed</dt><dd>{relTime(t.completed_at)}{person(t.completed_by_id) ? ` by ${fullName(person(t.completed_by_id))}` : ""}</dd></div>}
      </dl>

      {notes && <div className="td-notes"><h4>Notes</h4><p>{notes}</p></div>}

      {loc && (loc.geometry || loc.latitude) && (
        <div className="td-map"><LocationMap items={[{ id: loc.id, label: loc.label || "", geometry: loc.geometry, latitude: loc.latitude, longitude: loc.longitude }]} height={180} interactive={false} /></div>
      )}

      <footer className="td-foot">
        {!closed && <PMButton variant="outline-end" size="sm" onClick={() => m.cancelReminder(t.id)}>Cancel task</PMButton>}
        <span className="spacer" />
        <PMButton variant="text" size="sm" icon="trash" onClick={() => setConfirmDelete(true)}>Delete</PMButton>
      </footer>

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this task?" subtitle="It's removed for everyone on the team. This can't be undone." size="sm"
        footer={<PMFormActions onCancel={() => setConfirmDelete(false)} onConfirm={async () => { if (await m.deleteReminder(t.id)) { setConfirmDelete(false); onClose(); } }} confirmLabel="Delete task" danger />}>
        <p className="muted" style={{ margin: 0 }}>“{title}”</p>
      </Modal>
    </aside>
  );
}
